import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import { TIERS, STATE_NAME } from "../data/broker";
import { EMPLOYER_KIND, industryColor, industryOf } from "../data/employer";
import { STATE_NAME_TO_CODE } from "../data/states";
import ContactPopup from "./ContactPopup";

const US_STATES_GEOJSON =
  "https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json";

const US_CENTER = [39.5, -98.35];
const US_ZOOM = 4;
const US_BOUNDS = [
  [15, -170],
  [72, -50],
];

// ---- State choropleth helpers -------------------------------------------
function colorForCount(count, max, tint) {
  if (!count) return "#0e0e11";
  const t = Math.min(1, Math.log(count + 1) / Math.log((max || 1) + 1));
  const alpha = 0.15 + t * 0.6;
  return hexToRgba(tint, alpha);
}
function hexToRgba(hex, alpha) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ---- Contact marker icon ------------------------------------------------
function buildContactIcon(tier, selected, dimmed, colorOverride) {
  const meta = TIERS[tier] || TIERS.t5;
  const color = colorOverride || meta.color;
  const glow = colorOverride ? `${colorOverride}b3` : meta.glow;
  const cls = `acc-node ${selected ? "acc-node-selected" : ""} ${dimmed ? "acc-node-dim" : ""}`;
  const html = `
    <div class="${cls}" style="--node-color:${color};--node-glow:${glow};">
      <span class="acc-node-ring"></span>
      <span class="acc-node-core"></span>
    </div>
  `;
  return L.divIcon({
    className: "sales-node-wrap",
    html,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
  });
}

function buildClusterIcon(cluster) {
  const count = cluster.getChildCount();
  const size = count < 10 ? 32 : count < 50 ? 40 : count < 150 ? 50 : 60;
  const html = `<div class="tier-cluster" style="width:${size}px;height:${size}px;"><span>${count}</span></div>`;
  return L.divIcon({ html, className: "tier-cluster-wrap", iconSize: [size, size] });
}

function FlyToNode({ node }) {
  const map = useMap();
  useEffect(() => {
    if (!node || !node.coords) return;
    // A zero-sized container (e.g. the pane is hidden) makes Leaflet's
    // animation maths produce NaN and throw, so skip until it has a size.
    const size = map.getSize();
    if (!size || size.x === 0 || size.y === 0) return;
    const [lat, lng] = node.coords;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    map.flyTo(node.coords, Math.max(map.getZoom(), 9), { duration: 0.9 });
  }, [node, map]);
  return null;
}

function FitBoundsOnIsolate({ contacts, isolatedIds }) {
  const map = useMap();
  useEffect(() => {
    if (!isolatedIds || isolatedIds.size < 2) return;
    const pins = contacts.filter((c) => isolatedIds.has(c.id) && c.coords);
    if (pins.length < 2) return;
    const bounds = L.latLngBounds(pins.map((c) => c.coords));
    map.fitBounds(bounds, { padding: [80, 80], maxZoom: 8, animate: true });
  }, [contacts, isolatedIds, map]);
  return null;
}

function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

// Opens the detail popup for whatever the sidebar just selected, so a contact
// can be inspected from the list without hunting for its marker. Waits for the
// flyTo to settle and for the marker to actually be on the map (a clustered
// marker only mounts once the cluster breaks apart).
// Opens (and keeps open) the detail popup for whatever the sidebar just
// selected. The cluster group tears markers down and rebuilds them as the
// flyTo settles, which destroys a freshly opened popup — so this re-asserts the
// popup until the churn stops, while still honouring a deliberate close.
function OpenPopupOnSelect({ selectedId, markerRefs, clusterRef }) {
  const map = useMap();
  useEffect(() => {
    // Selection cleared (or changed) — never leave an orphaned popup behind.
    map.closePopup();
    if (!selectedId) return undefined;

    let stopped = false;
    let ticks = 0;
    let interval = null;
    let programmatic = false;

    // A short map (phone landscape) needs longer: the marker can only mount
    // once the cluster group breaks apart, which takes more zoom steps in a
    // small viewport.
    const size0 = map.getSize();
    const maxTicks = size0 && size0.y < 420 ? 60 : 24;

    const stop = () => {
      stopped = true;
      if (interval) clearInterval(interval);
    };

    // A close is the user's if the marker is still on the map AND we did not
    // cause it ourselves; a close from marker recycling must be re-asserted.
    const onPopupClose = (e) => {
      if (programmatic) return;
      const src = e.popup && e.popup._source;
      if (src && src._map) stop();
    };
    map.on("popupclose", onPopupClose);

    const openFor = (marker) => {
      programmatic = true;
      map.closePopup();
      marker.openPopup();
      programmatic = false;
    };

    const assert = () => {
      if (stopped || ticks++ > maxTicks) {
        stop();
        return;
      }
      const size = map.getSize();
      if (!size || size.x === 0) return;

      const marker = markerRefs.current.get(selectedId);
      if (!marker) return;

      if (marker._map) {
        if (!marker.isPopupOpen()) openFor(marker);
        return;
      }

      // Still swallowed by a cluster. Re-arm the reveal periodically: a single
      // attempt can be lost when it collides with the in-flight flyTo.
      const group = clusterRef.current;
      if (group && typeof group.zoomToShowLayer === "function" && ticks % 5 === 1) {
        map.stop();
        group.zoomToShowLayer(marker, () => {});
      }
    };

    const start = setTimeout(() => {
      assert();
      interval = setInterval(assert, 300);
    }, 600);

    return () => {
      stopped = true;
      clearTimeout(start);
      if (interval) clearInterval(interval);
      map.off("popupclose", onPopupClose);
    };
  }, [selectedId, map, markerRefs, clusterRef]);
  return null;
}

function ZoomWatcher({ onZoom }) {
  const map = useMap();
  useEffect(() => {
    const handler = () => onZoom(map.getZoom());
    map.on("zoomend", handler);
    handler();
    return () => map.off("zoomend", handler);
  }, [map, onZoom]);
  return null;
}

function MapClickCatcher({ enabled, onPick }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => onPick([e.latlng.lat, e.latlng.lng]);
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [enabled, map, onPick]);
  return null;
}

export default function SalesMap({
  contacts,
  connections,
  visibleTiers,
  activeTerritory,
  showStates,
  choropleth,
  choroplethTint,
  stateCounts,
  selectedId,
  onSelect,
  focused,
  onStateHover,
  hoveredState,
  focusedFirmId,
  isolatedIds,
  salesTeamIds,
  // Networks mode extras
  networksMode,
  lifecycleMode,
  onLifecycleStateSelect,
  lifecycleState,
  selectedTpa,
  tpaCoverageByState,
  stateMarketTiers,
  onNetworkStateSelect,
  focusedNetworkState,
  addMode,
  onMapPick,
  onEditContact,
  onDeleteContact,
  onAddConnection,
  onViewConnections,
  contactConnectionCount, // map: contactId -> number
}) {
  const geoRef = useRef(null);
  const markerRefs = useRef(new Map());
  const clusterRef = useRef(null);
  const [statesData, setStatesData] = useState(null);
  const [zoom, setZoom] = useState(US_ZOOM);
  // Keep the standard (non-choropleth) view as dark as the choropleth view at
  // country zoom, but let the basemap show through on drill-in so geography
  // and city labels stay readable.
  const baseFillOpacity = zoom >= 7 ? 0.45 : zoom >= 6 ? 0.7 : 0.9;

  useEffect(() => {
    let cancelled = false;
    fetch(US_STATES_GEOJSON)
      .then((r) => r.json())
      .then((raw) => {
        if (cancelled) return;
        // Attach 2-letter state_code to each feature for coloring/hover.
        for (const f of raw.features) {
          const name = f.properties?.name;
          f.properties.state_code = STATE_NAME_TO_CODE[name] || "";
        }
        setStatesData(raw);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const maxCount = useMemo(
    () => Math.max(1, ...Object.values(stateCounts || {})),
    [stateCounts],
  );

  // Shaded states are opaque at country zoom but must not paint over city
  // geography on drill-in (the amber employer tint made the map unreadable).
  const choroplethFill = zoom >= 7 ? 0.28 : zoom >= 6 ? 0.6 : 1;

  const styleFor = (feature) => {
    const code = feature.properties.state_code;
    const count = stateCounts[code] || 0;
    const isFocused = networksMode && focusedNetworkState === code;
    const isHovered = !!code && hoveredState === code;
    if (isHovered && !isFocused) {
      return {
        weight: 1.6,
        color: "rgba(34,211,238,0.85)",
        fillColor: choropleth ? colorForCount(count, maxCount, choroplethTint) : "#16161c",
        fillOpacity: choropleth
          ? (count === 0 && networksMode ? 0.35 : choroplethFill)
          : baseFillOpacity,
      };
    }
    return {
      weight: isFocused ? 2.2 : 0.8,
      color: isFocused ? "rgba(34,197,94,0.9)" : "rgba(255,255,255,0.14)",
      fillColor: choropleth ? colorForCount(count, maxCount, choroplethTint) : "#0e0e11",
      fillOpacity: choropleth
        ? (count === 0 && networksMode ? 0.35 : choroplethFill)
        : baseFillOpacity,
    };
  };

  const onEachFeature = (feature, layer) => {
    layer.on({
      mouseover: () => {
        // Emulated hover events fire during load on touch devices and would
        // leave a state stuck in the readout, so taps drive it there instead.
        if (window.matchMedia("(hover: none)").matches) return;
        onStateHover && onStateHover(feature.properties.state_code);
      },
      mouseout: () => {
        // On a touchscreen a tap fires mouseover -> click -> mouseout, which
        // would wipe the readout the tap just set.
        if (window.matchMedia("(hover: none)").matches) return;
        onStateHover && onStateHover(null);
      },
      click: () => {
        // A finger has no hover, so a tap also drives the state readout.
        onStateHover && onStateHover(feature.properties.state_code);
        if (networksMode && onNetworkStateSelect) {
          onNetworkStateSelect(feature.properties.state_code);
        }
        if (lifecycleMode && onLifecycleStateSelect) {
          // Always scope to the clicked state; the overlay's "All" control
          // returns to global (avoids reading a stale value in this closure).
          onLifecycleStateSelect(feature.properties.state_code);
        }
      },
    });
  };

  const visibleContacts = useMemo(() => {
    // Isolation mode wins over all other filters — show ONLY those ids.
    if (isolatedIds && isolatedIds.size > 0) {
      return contacts.filter((c) => c.coords && isolatedIds.has(c.id));
    }
    return contacts.filter((c) => {
      if (!c.coords) return false;
      // Sales team is always visible (bypass tier/territory/firm filters).
      if (salesTeamIds && salesTeamIds.has(c.id)) return true;
      // TPA contacts bypass the tier/territory filters — their visibility
      // is governed by the Networks tab "On Map" toggle upstream.
      if (c.kind === "tpa_contact") return true;
      // Employers sit outside the broker tier ladder — their visibility is
      // governed by the Employers panel's on-map toggle + industry filter.
      if (c.kind === EMPLOYER_KIND) return true;
      if (!visibleTiers[c.tier]) return false;
      if (activeTerritory !== "all" && c.territory !== activeTerritory) return false;
      if (focusedFirmId && c.firm_id !== focusedFirmId) return false;
      return true;
    });
  }, [contacts, visibleTiers, activeTerritory, focusedFirmId, isolatedIds, salesTeamIds]);

  // Contacts index (by id) for O(1) lookups when rendering edges.
  const contactsById = useMemo(() => {
    const m = new Map();
    for (const c of contacts) m.set(c.id, c);
    return m;
  }, [contacts]);

  // Render edges only when a contact is selected — the neural-network view.
  const activeEdges = useMemo(() => {
    if (!selectedId || !connections || connections.length === 0) return [];
    const edges = [];
    for (const e of connections) {
      if (e.contact_a !== selectedId && e.contact_b !== selectedId) continue;
      const peerId = e.contact_a === selectedId ? e.contact_b : e.contact_a;
      const a = contactsById.get(selectedId);
      const b = contactsById.get(peerId);
      if (a?.coords && b?.coords) {
        edges.push({ id: e.id, positions: [a.coords, b.coords], peer: b });
      }
    }
    return edges;
  }, [selectedId, connections, contactsById]);

  // Endpoints of every connection must NEVER be swallowed by a cluster —
  // otherwise the dashed line lands on numbered circles and the pins vanish.
  // NOTE: this set is intentionally independent of selectedId so a marker never
  // moves between the cluster group and the top-level list on selection (that
  // re-parenting unmounts the Marker and destroys its freshly opened Popup).
  const edgeEndpointIds = useMemo(() => {
    const s = new Set();
    for (const e of connections || []) {
      s.add(e.contact_a);
      s.add(e.contact_b);
    }
    return s;
  }, [connections]);

  // Split visible contacts: unclustered (edge endpoints) vs clustered (rest).
  const [unclusteredContacts, clusteredContacts] = useMemo(() => {
    const uc = [];
    const cc = [];
    for (const c of visibleContacts) {
      // TPA contacts render outside the cluster so their indigo pins are
      // always individually visible instead of collapsing into cluster bubbles.
      if (edgeEndpointIds.has(c.id) || c.kind === "tpa_contact" || c.kind === EMPLOYER_KIND) uc.push(c);
      else cc.push(c);
    }
    return [uc, cc];
  }, [visibleContacts, edgeEndpointIds]);

  return (
    <div className="relative w-full h-full" data-testid="map-container">
      <MapContainer
        center={US_CENTER}
        zoom={US_ZOOM}
        minZoom={3}
        maxZoom={12}
        maxBounds={US_BOUNDS}
        maxBoundsViscosity={0.6}
        zoomControl
        attributionControl={false}
        closePopupOnClick={false}
        className="w-full h-full bg-[#09090B]"
        style={{ width: "100%", height: "100%" }}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
          className="vh-dark-tiles"
          maxZoom={16}
        />
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
          className="vh-dark-labels"
          maxZoom={16}
          minZoom={7}
        />
        {showStates && statesData && (
          <GeoJSON
            key={`states-${choropleth ? "cho" : "base"}-${lifecycleMode ? "lc" : ""}-${
              networksMode ? "net" : ""
            }`}
            ref={geoRef}
            data={statesData}
            style={styleFor}
            onEachFeature={onEachFeature}
          />
        )}

        <MarkerClusterGroup
          ref={clusterRef}
          chunkedLoading
          showCoverageOnHover={false}
          spiderfyOnMaxZoom
          disableClusteringAtZoom={8}
          maxClusterRadius={45}
          iconCreateFunction={buildClusterIcon}
        >
          {clusteredContacts.map((c) => (
            <Marker
              key={c.id}
              position={c.coords}
              ref={(m) => {
                if (m) markerRefs.current.set(c.id, m);
                else markerRefs.current.delete(c.id);
              }}
              icon={buildContactIcon(c.tier, c.id === selectedId, false,
                c.kind === EMPLOYER_KIND ? industryColor(industryOf(c)) : null)}
              eventHandlers={{ click: () => onSelect(c.id) }}
            >
              <Popup
              className="usac-popup"
              closeButton
              autoPan
              autoPanPaddingTopLeft={[60, 80]}
              autoPanPaddingBottomRight={[40, 40]}
              closeOnClick={false}
              autoClose={false}
            >
                <ContactPopup
                  contact={c}
                  stateName={STATE_NAME[c.state] || c.state}
                  stateNetworks={tpaCoverageByState?.[c.state]}
                  stateMarketTier={stateMarketTiers?.[c.state]}
                  connectionCount={
                    contactConnectionCount ? contactConnectionCount.get(c.id) || 0 : 0
                  }
                  onEdit={onEditContact}
                  onDelete={onDeleteContact}
                  onAddConnection={onAddConnection}
                  onViewConnections={onViewConnections}
                />
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>

        {/* Edge endpoints — rendered OUTSIDE the cluster group so the dashed
            neural-network line always terminates on a visible node. */}
        {unclusteredContacts.map((c) => (
          <Marker
            key={c.id}
            position={c.coords}
            ref={(m) => {
              if (m) markerRefs.current.set(c.id, m);
              else markerRefs.current.delete(c.id);
            }}
            icon={buildContactIcon(c.tier, c.id === selectedId, false,
              c.kind === EMPLOYER_KIND ? industryColor(industryOf(c)) : null)}
            eventHandlers={{ click: () => onSelect(c.id) }}
            zIndexOffset={1000}
          >
            <Popup
              className="usac-popup"
              closeButton
              autoPan
              autoPanPaddingTopLeft={[60, 80]}
              autoPanPaddingBottomRight={[40, 40]}
              closeOnClick={false}
              autoClose={false}
            >
              <ContactPopup
                contact={c}
                stateName={STATE_NAME[c.state] || c.state}
                stateNetworks={tpaCoverageByState?.[c.state]}
                stateMarketTier={stateMarketTiers?.[c.state]}
                connectionCount={
                  contactConnectionCount ? contactConnectionCount.get(c.id) || 0 : 0
                }
                onEdit={onEditContact}
                onDelete={onDeleteContact}
                onAddConnection={onAddConnection}
                onViewConnections={onViewConnections}
              />
            </Popup>
          </Marker>
        ))}

        {/* Neural-network edges — only when a contact is selected */}
        {activeEdges.map((e) => (
          <Polyline
            key={e.id}
            positions={e.positions}
            pathOptions={{
              color: "#22D3EE",
              weight: 1.4,
              opacity: 0.75,
              dashArray: "4 6",
            }}
          />
        ))}

        <FlyToNode node={focused} />
        <OpenPopupOnSelect
          selectedId={selectedId}
          markerRefs={markerRefs}
          clusterRef={clusterRef}
        />
        <FitBoundsOnIsolate contacts={contacts} isolatedIds={isolatedIds} />
        <MapResizer />
        <ZoomWatcher onZoom={setZoom} />
        <MapClickCatcher enabled={addMode} onPick={onMapPick} />
      </MapContainer>
      {addMode && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 border border-cyan-400/50 bg-[#0a0a0c]/90 font-mono text-[11px] tracking-[0.2em] uppercase text-cyan-300"
          data-testid="add-mode-banner"
        >
          Click the map to drop this contact
        </div>
      )}
      {!addMode && !networksMode && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] px-3 py-1.5 border border-white/10 bg-[#0a0a0c]/80 backdrop-blur font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-400 pointer-events-none"
          data-testid="map-hint"
        >
          Click any <span className="text-cyan-300">cluster</span> to drill in · scroll or +/- to zoom
        </div>
      )}
      {networksMode && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] px-3 py-1.5 border border-emerald-400/40 bg-[#0a0a0c]/85 backdrop-blur font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-200 pointer-events-none"
          data-testid="networks-mode-hint"
        >
          {selectedTpa === "all" ? "All TPAs" : selectedTpa} · Click any state for carrier detail
        </div>
      )}
      {/* Networks-mode state detail card */}
      {networksMode && focusedNetworkState && (
        <NetworkStateCard
          state={focusedNetworkState}
          info={tpaCoverageByState?.[focusedNetworkState]}
          marketTier={stateMarketTiers?.[focusedNetworkState]}
          selectedTpa={selectedTpa}
          onClose={() => onNetworkStateSelect && onNetworkStateSelect(null)}
        />
      )}
      {/* Non-invasive brand watermark */}
      <img
        src="/virtue-logo.png"
        alt=""
        aria-hidden="true"
        data-testid="map-watermark"
        className="absolute bottom-3 right-3 z-[400] h-16 w-auto opacity-20 pointer-events-none select-none drop-shadow-[0_0_12px_rgba(12,120,137,0.35)]"
      />
    </div>
  );
}

/**
 * Floating card in the top-right of the map showing the carrier breakdown
 * for whichever state the user just clicked while in Networks mode.
 */
function NetworkStateCard({ state, info, marketTier, selectedTpa, onClose }) {
  if (!state) return null;
  const stateFull = STATE_NAME[state] || state;
  const tpas = info?.tpas || [];
  const filtered = selectedTpa === "all" ? tpas : tpas.filter((t) => t.tpa === selectedTpa);
  return (
    <div
      className="absolute top-14 right-3 z-[600] w-[320px] max-w-[80vw] border border-emerald-400/40 bg-[#0a0a0c]/95 backdrop-blur p-4 shadow-2xl"
      data-testid="network-state-card"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-emerald-200">
            Network Coverage
          </div>
          <div className="font-bold text-zinc-50 text-[14px] tracking-[0.05em] mt-0.5">
            {stateFull}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500 hover:text-zinc-200"
          data-testid="network-state-card-close"
        >
          ✕
        </button>
      </div>

      {/* Market tier ranking — from tiers CSV. Shown above coverage detail. */}
      {marketTier && (marketTier.tier_1 || marketTier.tier_2 || marketTier.tier_3) && (
        <div
          className="mb-3 border border-amber-400/40 bg-amber-500/5 p-2"
          data-testid="network-state-market-tiers"
        >
          <div className="font-mono text-[8.5px] tracking-[0.2em] uppercase text-amber-200 mb-1.5">
            Market Tier · Directional
          </div>
          <div className="grid grid-cols-3 gap-1 mb-1.5">
            <TierPill rank="T1" name={marketTier.tier_1} />
            <TierPill rank="T2" name={marketTier.tier_2} />
            <TierPill rank="T3" name={marketTier.tier_3} />
          </div>
          {marketTier.independent_tpas && (
            <div className="text-[10px] leading-tight">
              <span className="font-mono uppercase tracking-[0.15em] text-amber-200/80 text-[9px]">
                Indep TPAs
              </span>
              <span className="text-zinc-300"> · {marketTier.independent_tpas}</span>
            </div>
          )}
          {marketTier.notes && (
            <div className="text-[10px] text-zinc-400 italic mt-1">{marketTier.notes}</div>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="font-mono text-[10.5px] leading-relaxed tracking-[0.06em] text-zinc-500 py-2">
          {selectedTpa === "all"
            ? "No confirmed TPA network access in this state yet."
            : `${selectedTpa} does not have confirmed carrier access in ${state}.`}
        </div>
      ) : (
        <div className="space-y-2 max-h-[45vh] overflow-auto pr-1">
          <div className="font-mono text-[8.5px] tracking-[0.2em] uppercase text-emerald-200/80">
            Virtue Health TPA Access
          </div>
          {filtered.map((row) => (
            <div key={row.tpa} className="border border-white/10 p-2">
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-200 mb-1">
                {row.tpa}
              </div>
              <div className="flex flex-wrap gap-1">
                {row.carriers.map((c) => (
                  <span
                    key={c}
                    className="px-1.5 py-0.5 border border-emerald-400/40 bg-emerald-500/10 text-emerald-100 text-[10px] font-mono tracking-[0.05em]"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 font-mono text-[8.5px] tracking-[0.16em] uppercase text-zinc-600">
        {filtered.length} TPA{filtered.length === 1 ? "" : "s"} · Click another state to compare
      </div>
    </div>
  );
}

function TierPill({ rank, name }) {
  if (!name) {
    return (
      <div className="border border-white/5 px-1.5 py-1 text-center">
        <div className="font-mono text-[8px] tracking-[0.2em] text-zinc-600 uppercase">{rank}</div>
        <div className="text-[9.5px] text-zinc-700">—</div>
      </div>
    );
  }
  return (
    <div className="border border-amber-400/40 bg-amber-500/10 px-1.5 py-1 text-center" title={name}>
      <div className="font-mono text-[8px] tracking-[0.2em] text-amber-300 uppercase">{rank}</div>
      <div className="text-[10px] text-amber-100 leading-tight truncate">{name}</div>
    </div>
  );
}
